import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { PreApprovalRecipient, PreApprovalMetadata } from '@/types/pre-approval';

const log = createComponentLogger('RecipientAccessService', 'service');

export class RecipientAccessService {
  /**
   * Generate a cryptographically secure random token
   */
  private static generateSecureToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate unique access token for a recipient
   */
  static generateRecipientToken(
    preApprovalId: string,
    recipientType: 'borrower' | 'co_borrower' | 'partner',
    recipientEmail: string,
    recipientName: string,
    tokenExpiresAt: string
  ): PreApprovalRecipient {
    const token = this.generateSecureToken();
    const now = new Date();
    const expiresAt = new Date(tokenExpiresAt);

    if (Number.isNaN(expiresAt.getTime())) {
      throw new Error('Invalid pre-approval recipient token expiration');
    }

    return {
      type: recipientType,
      email: recipientEmail,
      name: recipientName,
      token,
      tokenExpiresAt: expiresAt.toISOString(),
      sentAt: now.toISOString(),
      firstViewedAt: null,
      lastViewedAt: null,
      viewCount: 0
    };
  }

  /**
   * Add recipients to pre-approval metadata
   * This stores the recipient tokens in the pre_approvals.metadata JSONB field
   */
  static async addRecipientsToMetadata(
    preApprovalId: string,
    recipients: PreApprovalRecipient[]
  ): Promise<void> {
    try {
      log.info('Adding recipients to pre-approval metadata', {
        preApprovalId,
        recipientCount: recipients.length,
        recipientTypes: recipients.map(r => r.type)
      });

      // Get current metadata
      const { data: preApproval, error: fetchError } = await supabase
        .from('pre_approvals')
        .select('metadata')
        .eq('id', preApprovalId)
        .single();

      if (fetchError) {
        log.error('Failed to fetch pre-approval metadata', { preApprovalId, error: fetchError });
        throw fetchError;
      }

      // Merge recipients into metadata
      const currentMetadata = (preApproval?.metadata as PreApprovalMetadata) || {};
      const updatedMetadata: PreApprovalMetadata = {
        ...currentMetadata,
        recipients
      };

      // Update pre-approval with new metadata
      const { error: updateError } = await supabase
        .from('pre_approvals')
        .update({ metadata: updatedMetadata })
        .eq('id', preApprovalId);

      if (updateError) {
        log.error('Failed to update pre-approval metadata', { preApprovalId, error: updateError });
        throw updateError;
      }

      log.info('Successfully added recipients to metadata', {
        preApprovalId,
        recipientCount: recipients.length
      });
    } catch (error) {
      log.error('Error in addRecipientsToMetadata', {
        preApprovalId,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Validate recipient token and get pre-approval ID + recipient info
   */
  static async validateRecipientToken(token: string): Promise<{
    preApprovalId: string;
    recipient: PreApprovalRecipient;
  } | null> {
    try {
      log.debug('Validating recipient token', { tokenPrefix: token.substring(0, 8) });

      const { data, error } = await supabase.functions.invoke('public-preapproval-access', {
        body: { token }
      });

      if (error) {
        log.error('Failed to validate token with server resolver', { error });
        return null;
      }

      if (!data?.success || data.recipientIndex === null || data.recipientIndex === undefined) {
        log.warn('Recipient token rejected by server resolver', {
          tokenPrefix: token.substring(0, 8),
          reason: data?.error
        });
        return null;
      }

      const resolvedRecipient = data.preApproval?.metadata?.resolvedRecipient as
        | Pick<PreApprovalRecipient, 'type' | 'email' | 'name'>
        | undefined;
      if (!resolvedRecipient) {
        log.warn('Server resolver returned missing resolved recipient', {
          tokenPrefix: token.substring(0, 8),
          preApprovalId: data.preApproval?.id,
          recipientIndex: data.recipientIndex
        });
        return null;
      }

      return {
        preApprovalId: data.preApproval.id,
        recipient: {
          type: resolvedRecipient.type,
          email: resolvedRecipient.email || '',
          name: resolvedRecipient.name || '',
          token,
          tokenExpiresAt: data.preApproval.expires_at || '',
          sentAt: '',
          firstViewedAt: null,
          lastViewedAt: null,
          viewCount: 0
        }
      };
    } catch (error) {
      log.error('Error validating recipient token', {
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }

  /**
   * Track a view by a specific recipient
   * Calls server-side edge function for secure tracking and notifications
   */
  static async trackRecipientView(
    token: string,
    userAgent?: string,
    referrer?: string
  ): Promise<boolean> {
    try {
      log.info('Tracking recipient view via server', { tokenPrefix: token.substring(0, 8) });

      const { data, error } = await supabase.functions.invoke('track-preapproval-view', {
        body: {
          token,
          userAgent: userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
          referrer: referrer || (typeof document !== 'undefined' ? document.referrer : undefined)
        }
      });

      if (error) {
        log.error('Server tracking failed', { error });
        return false;
      }

      log.info('View tracked successfully', {
        preApprovalId: data?.preApprovalId,
        isFirstView: data?.isFirstView
      });

      return data?.success || false;
    } catch (error) {
      log.error('Error tracking recipient view', {
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }

  /**
   * Get all recipients for a pre-approval with their view status
   */
  static async getRecipientsByPreApproval(preApprovalId: string): Promise<PreApprovalRecipient[]> {
    try {
      log.debug('Fetching recipients for pre-approval', { preApprovalId });

      const { data, error } = await supabase
        .from('pre_approvals')
        .select('metadata')
        .eq('id', preApprovalId)
        .single();

      if (error) {
        log.error('Failed to fetch recipients', { preApprovalId, error });
        return [];
      }

      const metadata = (data?.metadata as PreApprovalMetadata) || {};
      const recipients = metadata.recipients || [];

      log.info('Retrieved recipients', {
        preApprovalId,
        recipientCount: recipients.length
      });

      return recipients;
    } catch (error) {
      log.error('Error getting recipients', {
        preApprovalId,
        error: error instanceof Error ? error.message : String(error)
      });
      return [];
    }
  }
}
